import type { Filter } from '../../../../stores/filters';

export enum FilterActions {
  Disable = 'disable',
  Enable = 'enable',
  Hide = 'hide',
  Show = 'show',
  SetExpression = 'setExpression',
  Clear = 'clear',
  SetRequired = 'setRequired',
}

export interface FilterDependency {
  sourceFilter: string;
  condition: (filterState: Record<string, Filter>, expression: string) => boolean;
  action: FilterActions;
  targetFilter: string | string[];
  setValue?: string;
}

export const applyDependencies = (filterState: Record<string, Filter>) => {
  const newState = { ...filterState };

  filterDependencies.forEach((dependency: FilterDependency) => {
    const sourceFilter = newState[dependency.sourceFilter];
    if (sourceFilter && dependency.condition(newState, sourceFilter.expression)) {
      const targetFilters = Array.isArray(dependency.targetFilter)
        ? dependency.targetFilter
        : [dependency.targetFilter];

      targetFilters.forEach((targetFilter) => {
        switch (dependency.action) {
          case 'disable':
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              interactive: false,
            };
            break;
          case 'enable':
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              interactive: true,
            };
            break;
          case 'hide':
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              visible: false,
            };
            break;
          case 'show':
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              visible: true,
            };
            break;
          case 'setRequired':
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              required: true,
            };
            break;
          case 'setExpression':
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              interactive: true,
            };
            newState[targetFilter] = {
              ...newState[targetFilter],
              filterName: targetFilter,
              expression: dependency.setValue || '',
            };
            break;
        }
      });
    }
  });

  return newState;
};

// TODO: consider revisiting app-wide logic using this for possible replacement with server-side filter configs
export const filterDependencies: FilterDependency[] = [
  {
    sourceFilter: 'procedure_name_filter',
    condition: (filterState) => {
      return !filterState.procedure_name_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: ['claims_lookback_period_filter', 'procedure_decile_filter'],
  },
  {
    sourceFilter: 'patient_gender_filter',
    condition: (filterState) => {
      return !filterState.patient_gender_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: 'claims_lookback_period_filter',
  },
  {
    sourceFilter: 'patient_age_range_filter',
    condition: (filterState) => {
      return !filterState.patient_age_range_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: 'claims_lookback_period_filter',
  },
  {
    sourceFilter: 'patient_insurance_provider_name_filter',
    condition: (filterState) => {
      return !filterState.patient_insurance_provider_name_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: 'claims_lookback_period_filter',
  },
  {
    sourceFilter: 'patient_state_name_filter',
    condition: (filterState) => {
      return !filterState.patient_state_name_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: 'claims_lookback_period_filter',
  },
  {
    sourceFilter: 'patient_facility_name_filter',
    condition: (filterState) => {
      return !filterState.patient_facility_name_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: 'claims_lookback_period_filter',
  },
  {
    sourceFilter: 'number_of_patients_range_filter',
    condition: (filterState) => {
      return !filterState.number_of_patients_range_filter.enabled;
    },
    action: FilterActions.Disable,
    targetFilter: 'claims_lookback_period_filter',
  },
  {
    sourceFilter: 'diagnosis_name_filter',
    condition: (filterState) => !filterState.diagnosis_name_filter.enabled,
    action: FilterActions.Disable,
    targetFilter: ['claims_lookback_period_filter', 'diagnosis_decile_filter'],
  },

  {
    sourceFilter: 'prescribed_drug_name_filter',
    condition: (filterState) => !filterState.prescribed_drug_name_filter.enabled,
    action: FilterActions.Disable,
    targetFilter: ['claims_lookback_period_filter', 'prescribed_drug_decile_filter'],
  },

  {
    sourceFilter: 'prescribing_behavior_drug_name_filter',
    condition: (filterState) => !filterState.prescribing_behavior_drug_name_filter.enabled,
    action: FilterActions.Disable,
    targetFilter: [
      'prescribing_behavior_change_filter',
      'prescribing_behavior_prescription_type_filter',
      'claims_lookback_period_filter',
      'prescribing_behavior_decile_filter',
    ],
  },

  {
    sourceFilter: 'contextual_iab_taxonomy_id_filter',
    condition: (filterState) => !filterState.contextual_iab_taxonomy_id_filter.enabled,
    action: FilterActions.Disable,
    targetFilter: ['contextual_lookback_period_filter', 'contextual_iab_decile_filter'],
  },

  {
    sourceFilter: 'contextual_mesh_taxonomy_id_filter',
    condition: (filterState) => !filterState.contextual_mesh_taxonomy_id_filter.enabled,
    action: FilterActions.Disable,
    targetFilter: ['contextual_lookback_period_filter', 'contextual_mesh_decile_filter'],
  },

  {
    sourceFilter: 'search_keyword_filter',
    condition: (filterState) => filterState.search_keyword_filter.enabled,
    action: FilterActions.SetRequired,
    targetFilter: ['search_events_filter', 'smart_pixel_lookback_period_filter'],
  },
];
